import { useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';

/**
 * Which side of the business you are looking at: NEOM, the dumpers, the Riyadh
 * flatbeds - or all of them at once.
 *
 * There is deliberately one of these rather than a filter on every screen. The
 * chosen side is held in CurrentProjectContext and sent as X-Project-Id on
 * every request, so picking it here scopes every page opened afterwards.
 * Changing it invalidates the cached queries, so nothing from the previous side
 * is left on screen.
 */

interface ProjectRow {
  id: string;
  code: string;
  name_en: string;
}

/** Short names, because "NEOM Water Operations" does not fit a toolbar. */
const SHORT: Record<string, string> = {
  'PRJ-NEOM': 'NEOM',
  'PRJ-DUMPERS': 'Dumpers',
  'PRJ-RIYADH': 'Flatbed',
  'PRJ-RIYADH-WATER': 'Riyadh Water',
};

export function ProjectSwitcher({ className = '' }: { className?: string }) {
  const { project, setProject } = useCurrentProject();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ items: ProjectRow[] }>('/api/v1/projects')
      .then((r) => { if (!cancelled) setProjects(r.data.items ?? []); })
      // Someone without permission to list projects simply does not get the
      // switcher, rather than an error sitting on an unrelated page.
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  if (failed || projects.length === 0) return null;

  return (
    <label className={`inline-flex items-center gap-2 text-sm ${className}`}>
      <Layers className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">Side</span>
      <select
        value={project?.id ?? ''}
        onChange={(e) => {
          const row = projects.find((p) => p.id === e.target.value);
          setProject(row ? { id: row.id, code: row.code, nameEn: row.name_en } : null);
        }}
        className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground"
      >
        <option value="">All sides</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>{SHORT[p.code] ?? p.name_en}</option>
        ))}
      </select>
    </label>
  );
}
